import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Gift, Loader2, CheckCircle2, UserPlus, Sparkles, Stamp } from "lucide-react";
import { WalletButtons } from "@/pages/CustomerPortalPage";
import { API } from "@/lib/api";
import { DEFAULT_BRAND_COLOR } from "@/lib/brand";


function JoinRewardPanel({ vendor, brandColor, compact = false }) {
  const system = vendor.reward_system_info || {};
  const highlights = system.highlights || [];
  const recent = vendor.recent_redemptions || [];
  const descLines = (system.description || "").split("\n").filter(Boolean);

  return (
    <div
      className={compact ? "space-y-5" : "w-full max-w-md space-y-8"}
      data-testid="join-reward-panel"
    >
      {!compact && (
        <div className="text-center">
          {vendor.has_logo_header ? (
            <img
              src={`${API}/vendors/${vendor.id}/logo/header`}
              alt=""
              className="w-40 mx-auto mb-6 rounded-2xl object-cover shadow-lg animate-[fadeUp_0.5s_ease-out]"
            />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-white/20 mx-auto mb-6 flex items-center justify-center animate-[fadeUp_0.5s_ease-out]">
              <Gift size={28} className="text-white" />
            </div>
          )}
          <h3 className="text-3xl font-bold text-white tracking-tight font-display animate-[fadeUp_0.55s_ease-out]">
            {system.headline || "Start earning today"}
          </h3>
          <p className="text-white/65 text-sm mt-2 leading-relaxed animate-[fadeUp_0.6s_ease-out]">
            How {vendor.name}&apos;s rewards work
          </p>
        </div>
      )}

      {compact && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-quiet mb-1">How it works</p>
          <h3 className="text-xl font-bold text-ink tracking-tight font-display">
            {system.headline || "Start earning today"}
          </h3>
        </div>
      )}

      <div
        className={
          compact
            ? "rounded-2xl bg-white border border-line p-4 space-y-3"
            : "rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 p-5 space-y-3 animate-[fadeUp_0.65s_ease-out]"
        }
      >
        <div className="flex items-center gap-2">
          <Sparkles size={14} className={compact ? "text-ink" : "text-white/80"} />
          <p className={`text-xs font-semibold uppercase tracking-widest ${compact ? "text-quiet" : "text-white/70"}`}>
            Reward system
          </p>
        </div>
        <div className="space-y-1.5">
          {descLines.map((line, i) => (
            <p
              key={i}
              className={`text-sm leading-relaxed ${
                compact
                  ? i === 0 ? "font-medium text-ink" : "text-ink/60 pl-1"
                  : i === 0 ? "font-medium text-white" : "text-white/70 pl-1"
              }`}
            >
              {line.trim()}
            </p>
          ))}
          {!descLines.length && (
            <p className={`text-sm ${compact ? "text-ink/60" : "text-white/70"}`}>
              Join to start collecting rewards with every visit.
            </p>
          )}
        </div>
        {system.reward_expiry_days ? (
          <p className={`text-xs pt-1 ${compact ? "text-quiet" : "text-white/45"}`}>
            Rewards expire {system.reward_expiry_days} days after they&apos;re issued.
          </p>
        ) : null}
      </div>

      {highlights.length > 0 && (
        <div className={`space-y-2 ${compact ? "" : "animate-[fadeUp_0.7s_ease-out]"}`}>
          <p className={`text-xs font-semibold uppercase tracking-widest ${compact ? "text-quiet" : "text-white/60"}`}>
            What you can unlock
          </p>
          <ul className="space-y-2">
            {highlights.slice(0, 4).map((h, i) => (
              <li
                key={`${h.label}-${i}`}
                className={
                  compact
                    ? "flex items-center justify-between gap-3 rounded-xl bg-surface border border-line px-3 py-2.5"
                    : "flex items-center justify-between gap-3 rounded-xl bg-white/10 border border-white/10 px-3 py-2.5"
                }
              >
                <span className={`text-xs font-semibold shrink-0 ${compact ? "text-quiet" : "text-white/55"}`}>
                  {h.label}
                </span>
                <span className={`text-sm font-semibold text-right truncate ${compact ? "text-ink" : "text-white"}`}>
                  {h.summary}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={`space-y-3 ${compact ? "" : "animate-[fadeUp_0.8s_ease-out]"}`}>
        <div className="flex items-center gap-2">
          <Stamp size={14} className={compact ? "text-ink" : "text-white/80"} />
          <p className={`text-xs font-semibold uppercase tracking-widest ${compact ? "text-quiet" : "text-white/60"}`}>
            Recent rewards
          </p>
        </div>
        {recent.length === 0 ? (
          <p className={`text-sm ${compact ? "text-quiet" : "text-white/50"}`}>
            Be the first to redeem a reward here.
          </p>
        ) : (
          <ul className="space-y-2.5" data-testid="join-recent-redemptions">
            {recent.map((r, i) => (
              <li
                key={`${r.initials}-${r.redeemed_at || i}`}
                className={
                  compact
                    ? "rounded-2xl bg-white border border-line px-3.5 py-3 shadow-sm"
                    : "rounded-2xl bg-white px-3.5 py-3 shadow-sm text-ink"
                }
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 text-white"
                    style={{ backgroundColor: brandColor }}
                    aria-hidden
                  >
                    {r.initials || "?"}
                  </div>
                  <div className="min-w-0 pt-0.5">
                    <p className="text-sm font-semibold leading-snug">
                      <span className="tracking-wide">{r.initials}</span>
                      {" "}got{" "}
                      <span className="text-purple-700">{r.reward_summary}</span>
                      {" "}recently
                    </p>
                    {r.reward_title && r.reward_title !== r.reward_summary && (
                      <p className="text-[11px] text-quiet mt-0.5 truncate">{r.reward_title}</p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


const COUNTRY_CODES = [
  { code: "+44", flag: "🇬🇧", label: "UK" },
  { code: "+1", flag: "🇺🇸", label: "US" },
  { code: "+353", flag: "🇮🇪", label: "IE" },
  { code: "+91", flag: "🇮🇳", label: "IN" },
  { code: "+61", flag: "🇦🇺", label: "AU" },
  { code: "+33", flag: "🇫🇷", label: "FR" },
  { code: "+49", flag: "🇩🇪", label: "DE" },
  { code: "+34", flag: "🇪🇸", label: "ES" },
  { code: "+39", flag: "🇮🇹", label: "IT" },
  { code: "+971", flag: "🇦🇪", label: "AE" },
  { code: "+65", flag: "🇸🇬", label: "SG" },
  { code: "+27", flag: "🇿🇦", label: "ZA" },
];

export default function CustomerSignupPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+44");
  const [mobile, setMobile] = useState("");
  const [dob, setDob] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    axios.get(`${API}/portal/client-info?client_id=${slug}`)
      .then(({ data }) => setVendor(data))
      .catch(() => setVendor(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/portal/signup`, {
        client_id: slug,
        name: name.trim(),
        email: email.trim(),
        mobile_number: mobile.trim() ? `${countryCode}${mobile.replace(/\s+/g, "")}` : "",
        date_of_birth: dob,
        terms_accepted: agreed,
      });
      setSuccess(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <Loader2 size={24} className="animate-spin text-ink/30" />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface px-6">
        <div className="text-center">
          <p className="text-lg font-semibold text-ink">Business not found</p>
          <p className="text-sm text-ink/40 mt-1">The link may be incorrect or the business is no longer active.</p>
        </div>
      </div>
    );
  }

  if (!vendor.allow_customer_signup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface px-6">
        <div className="text-center">
          <p className="text-lg font-semibold text-ink">Registration Closed</p>
          <p className="text-sm text-ink/40 mt-1">{vendor.name} is not accepting new sign-ups at this time.</p>
          <Button onClick={() => navigate(`/${slug}`)} variant="outline" className="mt-4 rounded-full">Back to Portal</Button>
        </div>
      </div>
    );
  }

  const brandColor = vendor.brand_color || DEFAULT_BRAND_COLOR;

  if (success) {
    const lookupId = email.trim() || (mobile.trim() ? `${countryCode}${mobile.replace(/\s+/g, "")}` : "");
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface px-6 py-12">
        <div className="w-full max-w-sm text-center">
          <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={28} className="text-ink" />
          </div>
          <h2 className="text-2xl font-bold text-ink tracking-tight mb-2" data-testid="signup-success-title">
            {success.existing ? "Welcome back!" : "You're in!"}
          </h2>
          <p className="text-sm text-ink/50 mb-6" data-testid="signup-success-msg">
            {success.existing ? success.message : "Add your loyalty card to your wallet so it's always with you."}
          </p>
          <div className="text-left">
            <WalletButtons result={{ customer_id: success.customer_id }} clientId={vendor.id} brandColor={brandColor} />
          </div>
          <button
            onClick={() => navigate(`/${slug}?lookup=${encodeURIComponent(lookupId)}`)}
            className="mt-5 text-sm font-semibold text-ink/60 hover:text-ink underline"
            data-testid="go-to-portal-btn"
          >
            View my rewards
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex font-body bg-surface" data-testid="customer-signup-page">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-10">
            {vendor.has_logo_icon ? (
              <img src={`${API}/vendors/${vendor.id}/logo/icon`} alt="" className="w-11 h-11 rounded-xl object-cover" />
            ) : (
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: brandColor }}>
                <Gift size={20} className="text-white" />
              </div>
            )}
            <div>
              <h1 className="text-base font-bold text-ink leading-none font-display">{vendor.name}</h1>
              <p className="text-xs text-quiet mt-0.5">Rewards Programme</p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-ink tracking-tight font-display">Join & Earn</h2>
            <p className="text-sm text-ink/50 mt-1.5">Sign up to start collecting rewards with {vendor.name}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-ink/60 uppercase tracking-widest">Full Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="First name and Last name"
                className="h-11 rounded-xl border-ink/15 bg-white"
                data-testid="signup-name"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-ink/60 uppercase tracking-widest">Phone <span className="text-danger">*</span></Label>
              <div className="flex gap-2">
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="h-11 rounded-xl border border-ink/15 bg-white px-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  data-testid="signup-country-code"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                  ))}
                </select>
                <Input
                  type="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  required
                  placeholder="7700900000"
                  className="h-11 flex-1 rounded-xl border-ink/15 bg-white"
                  data-testid="signup-mobile"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-ink/60 uppercase tracking-widest">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@gmail.com"
                className="h-11 rounded-xl border-ink/15 bg-white"
                data-testid="signup-email"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-ink/60 uppercase tracking-widest">Date of Birth</Label>
              <Input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                className="h-11 rounded-xl border-ink/15 bg-white"
                data-testid="signup-dob"
              />
            </div>

            <div className="flex items-start gap-2.5 pt-1">
              <Checkbox
                id="terms"
                checked={agreed}
                onCheckedChange={(v) => setAgreed(!!v)}
                className="mt-0.5"
                data-testid="signup-terms"
              />
              <Label htmlFor="terms" className="text-xs text-ink/60 leading-relaxed font-normal cursor-pointer">
                I have read and agree to the{" "}
                <a href={`/${slug}/terms`} target="_blank" rel="noreferrer" className="text-ink font-semibold underline">Terms and Conditions</a>
                {" "}and{" "}
                <a href={`/${slug}/privacy`} target="_blank" rel="noreferrer" className="text-ink font-semibold underline">Privacy Policy</a>
              </Label>
            </div>

            {error && (
              <div className="px-4 py-2.5 rounded-xl bg-danger/10 border border-danger/30 text-danger text-sm" data-testid="signup-error">{error}</div>
            )}

            <Button
              type="submit"
              disabled={submitting || !name.trim() || !mobile.trim() || !agreed}
              className="w-full h-11 rounded-full text-white font-semibold text-sm"
              style={{ backgroundColor: brandColor }}
              data-testid="signup-submit"
            >
              {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <UserPlus size={16} className="mr-2" />}
              {submitting ? "Enrolling..." : "Enroll"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-ink/50 text-center">
            Already a member?{" "}
            <a href={`/${slug}`} className="text-ink font-semibold hover:text-purple-700" data-testid="go-to-lookup">Check your rewards</a>
          </p>

          <div className="lg:hidden mt-10 pt-8 border-t border-line">
            <JoinRewardPanel vendor={vendor} brandColor={brandColor} compact />
          </div>
        </div>
      </div>

      <div
        className="hidden lg:flex flex-1 items-center justify-center px-10 xl:px-14 py-12 overflow-y-auto"
        style={{ backgroundColor: brandColor }}
      >
        <JoinRewardPanel vendor={vendor} brandColor={brandColor} />
      </div>
    </div>
  );
}
